<template>
  <div class="bg-white layout-padding horizontal">
    <div class="q-py-md">
      <div class="block relative-position">
        <q-btn class="absolute absolute-top-right" icon="more_vert" color="faded" flat round dense>
          <q-popover>
            <q-list link class="no-border">
              <q-item
                @click.native="handleEdit"
                v-close-overlay
                v-is-user
              >
                <q-item-side icon="edit"/>
                <q-item-main :label="$t('edit')"/>
              </q-item>

              <q-item-separator/>

              <q-item
                v-if="project.active"
                @click.native="handleArchive"
                v-close-overlay
                v-is-user
              >
                <q-item-side icon="archive"/>
                <q-item-main :label="$t('archive')"/>
              </q-item>

              <q-item
                v-else
                @click.native="handleUnarchive"
                v-close-overlay
                v-is-user
              >
                <q-item-side icon="unarchive"/>
                <q-item-main :label="$t('unarchive')"/>
              </q-item>
            </q-list>
          </q-popover>
        </q-btn>
      </div>

      <div class="row gutter-x-md">
        <div class="col-auto gt-md">
          <placeholder-avatar class="q-display-1" width="96px" height="96px" :initials="projectInitials"/>
        </div>

        <div class="col">
          <div class="row full-height">
            <div class="col-12">
              <div class="row">
                <customer-indicator
                  v-if="hasCustomer"
                  clickable
                  hide-icon
                  :customer="customer"
                  @click="handleCustomerIndicatorClick"
                />
              </div>

              <div class="row items-center no-wrap q-ml-sm">
                <div>
                  <div class="project-details__color-indicator circle" :style="{ 'background-color': projectColor }"></div>
                </div>
                <span class="q-title text-dark on-right block ellipsis">{{ project.name }}</span>

                <div v-if="!project.active" class="on-right">
                  <q-chip dense color="warning" text-color="white">{{ $t('archived') }}</q-chip>
                </div>
              </div>

              <div v-if="estimatedHours" class="row items-center no-wrap q-mt-sm q-ml-sm">
                <progress
                  :max="estimatedHours"
                  :value="billableHours"
                  :style="progressBarStyle"
                  class="project-details__progress"
                >
                  <q-tooltip :offset="[0, 4]" v-html="progressTooltipText" />
                </progress>
              </div>
            </div>

            <div class="col-12 row items-end justify-between gutter-y-xs q-ml-sm q-mt-md q-mt-md-none">
              <div class="col-auto row gutter-x-md gutter-y-xs">
                <div v-if="startDate" class="col-12 col-sm-auto">
                  <span class="text-dark text-bold no-margin">{{ startDate }}</span>
                  <span class="text-faded text-weight-light">&nbsp;{{ $t('start_date') }}</span>
                </div>

                <div v-if="endDate" class="col-12 col-sm-auto">
                  <span class="text-dark text-bold no-margin">{{ endDate }}</span>
                  <span class="text-faded text-weight-light">&nbsp;{{ $t('end_date') }}</span>
                </div>

                <div v-if="showBudget" class="col-12 col-sm-auto">
                  <span class="text-dark text-bold no-margin">{{ budget }}</span>
                  <span class="text-faded text-weight-light">&nbsp;{{ $t('project_budget') }}</span>
                </div>

                <div class="col-12 col-sm-auto">
                  <span class="text-dark text-bold no-margin">{{ hourlyRate }}</span>
                  <span class="text-faded text-weight-light">&nbsp;{{ $t('hourly_rate') }}</span>
                </div>

                <div class="col-12 col-sm-auto">
                  <span class="text-dark text-bold no-margin">{{ billableHours }}</span>
                  <span class="text-faded text-weight-light">&nbsp;{{ $t('project_billable_hours') }}</span>
                </div>

                <div class="col-12 col-sm-auto">
                  <span class="text-dark text-bold no-margin">{{ openHours }}</span>
                  <span class="text-faded text-weight-light">&nbsp;{{ $tc('open_hour', openHours) }}</span>
                </div>

                <div v-if="estimatedHours" class="col-12 col-sm-auto">
                </div>
              </div>

              <div class="col-12 col-sm-auto q-mt-sm">
                <q-btn
                  rounded
                  outline
                  color="primary"
                  :label="$t('settle_hours')"
                  @click="handleSettleProject"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <project-modal ref="editProjectModal" :project="project" @save="handleSaveProject"/>
    <settle-project-modal ref="settleProjectModal" :project="project" @settle-project="handleConfirmSettleProject"/>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import CustomerIndicator from '../CustomerIndicator.vue'
  import PlaceholderAvatar from 'components/Common/PlaceholderAvatar.vue'
  import ProjectModal from './ProjectModal.vue'
  import SettleProjectModal from './SettleProjectModal.vue'
  import changeCase from 'change-case-object'
  import { date } from 'quasar'
  import { formatMoney } from 'src/utils/format'
  import parseDate from 'date-fns/parse'
  import { projectTypes } from 'src/utils/projectTypes'
  import { timeTracking } from 'src/config'

  export default {
    props: {
      project: {
        type: Object,
        required: true
      }
    },

    computed: {
      ...mapGetters(['activeTeam']),

      hasCustomer () {
        return !!this.project.customer_id && this.project.customer
      },

      customer () {
        return this.project.customer.data
      },

      startDate () {
        if (!this.project.start_date) {
          return null
        }

        return date.formatDate(parseDate(this.project.start_date.date), this.$t('date_format'))
      },

      endDate () {
        if (!this.project.end_date) {
          return null
        }

        return date.formatDate(parseDate(this.project.end_date.date), this.$t('date_format'))
      },

      budget () {
        return this.project.budget ? formatMoney(this.project.budget, null, 0, 2) : this.$t('no')
      },

      hourlyRate () {
        return this.project.rate ? formatMoney(this.project.rate, null, 0, 2) : this.$t('no')
      },

      estimatedHours () {
        return this.project.estimated_hours
      },

      openHours () {
        return Math.ceil(this.project.statistics.data.open_seconds / (60 * 60))
      },

      billableHours () {
        return Math.ceil(this.project.statistics.data.billable_seconds / (60 * 60))
      },

      projectInitials () {
        return this.project.name.split(/[\s,-]+/).slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase()
      },

      projectColor () {
        return this.project.color_hex
      },

      progressBarStyle () {
        return {
          '--bar-color': this.billableHours < this.estimatedHours ? '#68d391' : '#fc8181'
        }
      },

      progressTooltipText () {
        return [
          this.billableHours,
          'h',
          '/',
          this.estimatedHours,
          'h',
          this.$t('estimated_hours')
        ].join('&nbsp;')
      },

      showBudget () {
        return this.project.project_type !== projectTypes.NON_BILLABLE
      }
    },

    methods: {
      ...mapActions(['updateProject']),

      formatMoney,

      handleCustomerIndicatorClick () {
        this.$router.push({ name: 'customer', params: { customerId: this.customer.id } })
      },

      handleEdit () {
        this.$refs.editProjectModal.show()
      },

      handleArchive () {
        this.updateProject({
          projectId: this.project.id,
          project: { active: false }
        })
      },

      handleUnarchive () {
        this.updateProject({
          projectId: this.project.id,
          project: { active: true }
        })
      },

      handleSaveProject (project) {
        this.updateProject({
          projectId: this.project.id,
          project: changeCase.snake(project),
          include: ['customer']
        })
      },

      handleSettleProject () {
        this.$refs.settleProjectModal.show()
      },

      handleConfirmSettleProject ({ grouping, rounding }) {
        if (!this.groupingExceedsLimit(grouping)) {
          return this.createInvoiceFromUnbilledEntries({ grouping, rounding })
        }

        this.$q.dialog({
          title: this.$t('settle_project'),
          message: this.$t('settle_project_modal_grouping_individual_confirmation', {
            entries: this.project.statistics.data.open_time_entries_count
          }),
          ok: this.$t('confirm'),
          cancel: this.$t('change_selection')
        })
          .then(() => this.createInvoiceFromUnbilledEntries({ grouping, rounding }))
          .catch(() => {
            this.$refs.settleProjectModal.show()
          })
      },

      groupingExceedsLimit (grouping) {
        return grouping === 'individual' && this.project.statistics.data.open_time_entries_count > timeTracking.project_settlement_open_time_entries_limit
      },

      createInvoiceFromUnbilledEntries ({ grouping, rounding }) {
        this.$router.push({
          name: 'create-invoice',
          params: { teamId: this.activeTeam.id },
          query: {
            projects: [this.project.id],
            grouping,
            rounding
          }
        })
      }
    },

    components: {
      CustomerIndicator,
      PlaceholderAvatar,
      ProjectModal,
      SettleProjectModal
    }
  }
</script>

<style lang="stylus">
  @import '~variables'

  .project-details__stats
    background-color: lighten($blue-grey-1, 50%)
    box-shadow: inset 0 5px 5px -5px rgba(0, 0, 0, 0.1)
    padding: 24px 16px

  .project-details__stats > div:not(:last-child)
    border-right: 1px solid darken($blue-grey-1, 8%)

  .project-details__color-indicator
    width: 8px
    height: 8px

  .project-details__progress
    border 0
    height 8px
    border-radius 4px
    background-color #cbd5e0
    --bar-color $green-6

    &::-webkit-progress-bar
      border 0
      height 8px
      border-radius 4px
      background-color #cbd5e0

    &::-webkit-progress-value
      background-color var(--bar-color)
      border-radius 4px

    &::-moz-progress-bar
      background-color var(--bar-color)
      border-radius 4px
</style>
